// src/components/views/CustomerViewsDrawer.tsx
import React from 'react';
import {
  Drawer,
  Form,
  InputNumber,
  Input,
  Button,
  Timeline,
  Card,
  Space,
  Typography,
  Row,
  Col,
  message,
  Empty,
  Spin,
  Statistic,
  Popconfirm,
} from 'antd';
import { PlusOutlined, LineChartOutlined, ClockCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import { type Page } from '../../services/activities';
import {
  listViewsByCustomer,  // GET paginato
  createViews,          // POST { customerId, value, notes? }
  deleteViews,          // DELETE /p/views/{id}
  type ViewsDTO,        // { id, value, customerId, createdAt, notes }
} from '../../services/views';

const { Text } = Typography;

type Props = {
  open: boolean;
  onClose: () => void;
  customerId: number;
  onViewsChanged?: () => void;
};

type ViewForm = {
  value: number;
  notes?: string;
};

const CustomerViewsDrawer: React.FC<Props> = ({ open, onClose, customerId, onViewsChanged }) => {
  const [form] = Form.useForm<ViewForm>();
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [deletingId, setDeletingId] = React.useState<number | null>(null);

  const [pageIndex, setPageIndex] = React.useState(0);
  const [pageSize] = React.useState(20);
  const [hasMore, setHasMore] = React.useState(true);

  const [items, setItems] = React.useState<ViewsDTO[]>([]);
  const [totalSum, setTotalSum] = React.useState<number>(0); // 👈 somma TOTALE

  // Evita richieste concorrenti / race conditions
  const inFlight = React.useRef(false);
  const reqSeq = React.useRef(0);

  const loadPage = React.useCallback(async (page = 0) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    const myReq = ++reqSeq.current;

    try {
      const p: Page<ViewsDTO> = await listViewsByCustomer(customerId, page, pageSize);
      if (reqSeq.current !== myReq) return; // risposta obsoleta

      const content = p.content ?? [];
      setItems(prev => {
        const next = page === 0 ? content : [...prev, ...content];
        // 👇 Somma totale di TUTTI i record caricati finora
        const sumAll = next.reduce((acc, v) => acc + Number(v.value || 0), 0);
        setTotalSum(sumAll);
        return next;
      });

      setPageIndex(p.number ?? page);
      setHasMore((p.number ?? 0) < (p.totalPages ?? 1) - 1);
    } catch (e: any) {
      message.error(e?.message || 'Errore caricamento views');
    } finally {
      if (reqSeq.current === myReq) {
        setLoading(false);
        inFlight.current = false;
      }
    }
  }, [customerId, pageSize]);

  // Reset & prima pagina quando si apre o cambia il customer
  React.useEffect(() => {
    if (!open) return;
    setItems([]);
    setPageIndex(0);
    setHasMore(true);
    setTotalSum(0);
    // azzera eventuali richieste in volo
    reqSeq.current++;
    inFlight.current = false;
    loadPage(0);
  }, [open, customerId, loadPage]);

  const onSubmit = async (values: ViewForm) => {
    setSaving(true);
    try {
      await createViews({
        customerId,
        value: values.value,
        notes: values.notes?.trim() || undefined,
      });
      message.success('View aggiunta');
      form.resetFields();
      // ricarico da zero senza triggerare loop
      reqSeq.current++;
      inFlight.current = false;
      setItems([]);
      setPageIndex(0);
      setHasMore(true);
      setTotalSum(0);
      await loadPage(0);
      onViewsChanged?.();
    } catch (e: any) {
      message.error(e?.message || 'Errore salvataggio view');
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id: number) => {
    setDeletingId(id);
    try {
      await deleteViews(id);
      message.success('View eliminata');

      reqSeq.current++;
      inFlight.current = false;
      setItems([]);
      setPageIndex(0);
      setHasMore(true);
      setTotalSum(0);
      await loadPage(0);
      onViewsChanged?.();
    } catch (e: any) {
      message.error(e?.message || 'Errore eliminazione view');
    } finally {
      setDeletingId(null);
    }
  };

  const onLoadMore = async () => {
    if (!hasMore || loading || inFlight.current) return;
    await loadPage(pageIndex + 1);
  };

  return (
    <Drawer
      title="Views del Cliente"
      width={720}
      onClose={onClose}
      open={open}
      destroyOnClose
      bodyStyle={{ paddingBottom: 80 }}
      extra={
        <Space>
          {/* 👇 Etichetta aggiornata alla somma totale */}
          <Statistic title="Somma totale" value={totalSum} prefix={<LineChartOutlined />} />
        </Space>
      }
    >
      {/* Form inserimento */}
      <Card title="Aggiungi view" size="small" style={{ marginBottom: 16 }}>
        <Form<ViewForm>
          form={form}
          layout="vertical"
          initialValues={{ value: 0 }}
          onFinish={onSubmit}
        >
          <Row gutter={12}>
            <Col xs={24} sm={10}>
              <Form.Item
                name="value"
                label="Valore"
                rules={[{ required: true, message: 'Inserisci un numero' }]}
              >
                <InputNumber min={0} style={{ width: '100%' }} placeholder="Es. 1200" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={14} style={{ display: 'flex', alignItems: 'end' }}>
              <Form.Item style={{ flex: 1, marginRight: 8 }} name="notes" label="Note">
                <Input.TextArea rows={1} placeholder="Note opzionali" />
              </Form.Item>
              <Form.Item>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => form.submit()}
                  loading={saving}
                >
                  Aggiungi
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>

      {/* Timeline */}
      <Card title="Timeline" size="small">
        {loading && items.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 32 }}>
            <Spin />
          </div>
        ) : items.length === 0 ? (
          <Empty description="Nessuna view registrata" />
        ) : (
          <>
            <Timeline
              mode="left"
              items={items.map(v => ({
                key: v.id?.toString(),
                color: 'blue',
                dot: <ClockCircleOutlined />,
                label: v.createdAt ? dayjs(v.createdAt).format('DD/MM/YYYY HH:mm') : '-',
                children: (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                    <Space direction="vertical" size={4} style={{ flex: 1 }}>
                      <Text style={{ fontSize: 16, fontWeight: 600 }}>
                        {Number(v.value ?? 0).toLocaleString()}
                      </Text>
                      {v.notes && <Text type="secondary">{v.notes}</Text>}
                    </Space>
                    <Popconfirm
                      title="Eliminare questa view?"
                      description="Non verrà più conteggiata nei totali."
                      okText="Elimina"
                      cancelText="Annulla"
                      okButtonProps={{ danger: true }}
                      onConfirm={() => onDelete(v.id)}
                    >
                      <Button
                        type="text"
                        size="small"
                        danger
                        aria-label="Elimina view"
                        icon={<DeleteOutlined />}
                        loading={deletingId === v.id}
                        disabled={saving || deletingId !== null}
                      />
                    </Popconfirm>
                  </div>
                ),
              }))}
            />
            {hasMore && (
              <div style={{ textAlign: 'center', marginTop: 12 }}>
                <Button onClick={onLoadMore} disabled={loading || saving || inFlight.current}>
                  {loading || inFlight.current ? 'Caricamento…' : 'Carica altri'}
                </Button>
              </div>
            )}
          </>
        )}
      </Card>
    </Drawer>
  );
};

export default CustomerViewsDrawer;
